/**
 * Candidate Copy Utilities
 *
 * Reusable utilities for copying candidate data to clipboard in standardized formats.
 * These utilities can be used anywhere in the application where candidate data needs
 * to be copied to clipboard.
 */

import type { ProjectData } from '~/hooks/useProjectStore';
import type { Candidate, Configuration } from '~/types';
import { formatMarkdownTable } from './markdown-table-formatter';

/**
 * Format a value for display in tables
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    // Format currency values with commas
    if (Math.abs(value) >= 1000) {
      return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return value.toFixed(2);
  }
  return String(value);
}

/**
 * Generate cash flow table text for clipboard
 */
export function generateCashFlowTableText(
  candidate: Candidate,
  project: ProjectData,
  configuration: Configuration
): string {
  // Get shape ID from candidate
  const shapeId = candidate.millageInformation?.millageShape?.shapeId || 'Unknown Shape';

  // Get project and client info
  const projectName = project.metadata?.name || 'Unknown Project';
  const clientName = project.metadata?.clientName || 'Unknown Client';

  // Get number of bonds
  const bondCount = configuration.bondSeries?.length || 0;

  // Format cashflow header
  const headerFields = [
    'Payment Date',
    'Tax Year',
    'Fiscal Year End',
    'Millage Rate',
    'Total Revenue (Tax Year)',
    'Payment Revenue %',
    'Payment Revenue',
    'DRF Starting Balance',
    'Debt Payment Amount',
    'Existing Debt Principal',
    'Existing Debt Interest',
    'Revenue - Existing Debt',
    'Is Interest Only',
    'Interest Obligation',
    'Available for Bonds',
    'Total Bond Payment',
    'DRF Adjustment',
    'DRF Ending Balance',
    'DRF Net Change',
    'DRF Net Revenue',
  ];

  // Format bond payment columns dynamically - use bond series order
  const bondSeries = configuration.bondSeries || [];
  const bondHeaders: string[] = [];
  for (const bond of bondSeries) {
    const bondName = bond.name || `Bond ${bond.id || ''}`;
    bondHeaders.push(`${bondName} - Principal`, `${bondName} - Interest`, `${bondName} - Total`);
  }

  const allHeaders = [...headerFields, ...bondHeaders];

  // Format cashflow records
  const sortedCashFlows = [...(candidate.cashFlows || [])].sort(
    (a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime()
  );

  const rows = sortedCashFlows.map((cf) => {
    const baseRow: string[] = [
      cf.paymentDate,
      String(cf.relatedTaxBillYear),
      cf.relatedFiscalYearEndingDate,
      formatValue(cf.millageRate),
      formatValue(cf.totalRevenueForTaxYear),
      formatValue(`${(cf.paymentRevenuePercentage * 100).toFixed(1)}%`),
      formatValue(cf.paymentRevenuePortion),
      formatValue(cf.drfStartingBalance),
      formatValue(cf.debtPaymentAmount),
      formatValue(cf.existingDebtPrincipal),
      formatValue(cf.existingDebtInterest),
      formatValue(cf.revenueMinusExistingDebt),
      formatValue(cf.isInterestOnly),
      formatValue(cf.interestObligation),
      formatValue(cf.availableForBonds),
      formatValue(cf.totalBondPayment),
      formatValue(cf.drfAdjustment),
      formatValue(cf.drfEndingBalance),
      formatValue(cf.drfNetChange),
      formatValue(cf.drfNetRevenue),
    ];

    // Add bond payment columns - match by bondSeriesId
    const bondRow: string[] = [];
    for (const bond of bondSeries) {
      const bondPayment = cf.bondPayments?.find((bp) => bp.bondSeriesId === bond.id);
      if (bondPayment) {
        bondRow.push(
          formatValue(bondPayment.principal),
          formatValue(bondPayment.totalInterest),
          formatValue(bondPayment.totalPayment)
        );
      } else {
        bondRow.push('', '', '');
      }
    }

    return [...baseRow, ...bondRow];
  });

  // Format as markdown table
  const markdownTable = formatMarkdownTable(allHeaders, rows, {
    align: 'auto', // Auto-detect numeric columns for right alignment
    minColumnWidth: 3,
    maxColumnWidth: 50,
  });

  // Build the formatted text
  const textLines = [
    '='.repeat(80),
    'CASHFLOW INFORMATION',
    '='.repeat(80),
    '',
    `Client: ${clientName}`,
    `Project: ${projectName}`,
    `Shape ID: ${shapeId}`,
    `Configuration: ${configuration.name || `Config ${configuration.orderId || 1}`}`,
    `Number of Bonds: ${bondCount}`,
    '',
    '```markdown',
    markdownTable,
    '```',
    '',
    `Total Records: ${sortedCashFlows.length}`,
    `Date Range: ${sortedCashFlows[0]?.paymentDate || 'N/A'} to ${
      sortedCashFlows[sortedCashFlows.length - 1]?.paymentDate || 'N/A'
    }`,
  ];

  return textLines.join('\n');
}

/**
 * Generate tax year plan table text for clipboard
 */
export function generateTaxYearPlanTableText(
  candidate: Candidate,
  project: ProjectData,
  configuration: Configuration
): string {
  const taxYearPlan = candidate.taxYearBasedProjectPlan || [];
  if (taxYearPlan.length === 0) {
    return 'No tax year plan data available';
  }

  // Get shape ID from candidate
  const shapeId = candidate.millageInformation?.millageShape?.shapeId || 'Unknown Shape';

  // Get project and client info
  const projectName = project.metadata?.name || 'Unknown Project';
  const clientName = project.metadata?.clientName || 'Unknown Client';

  // Get bond names from configuration
  const bondNames = configuration.bondSeries?.map((bond) => bond.name) || [];

  // Sort tax year plan by tax year
  const sortedPlan = [...taxYearPlan].sort((a, b) => a.taxYear - b.taxYear);

  // Build headers
  const bondHeaders = bondNames.flatMap((name) => [`${name} Principal`, `${name} Principal (K)`]);

  const headers = [
    'Tax Year',
    'Taxable Value',
    'Millage',
    'Total Revenue',
    'Existing Debt',
    'Net Revenue',
    ...bondHeaders,
    'Total Bond Payments',
    'Total Bond Payments (K)',
    'Total Payments',
    'DRF Adjustment',
    'DRF Ending Balance',
  ];

  // Build rows
  const rows = sortedPlan.map((record) => {
    const bondPrincipalPairs = bondNames.flatMap((_, index) => {
      const principal = record.bondPayments?.[index] ?? 0;
      return [formatValue(principal), Math.round(principal / 1000).toString()];
    });

    return [
      String(record.taxYear),
      formatValue(record.taxableValue),
      formatValue(record.millage),
      formatValue(record.totalRevenue),
      formatValue(record.existingDebt),
      formatValue(record.netRevenue),
      ...bondPrincipalPairs,
      formatValue(record.totalBondPayments),
      Math.round(record.totalBondPayments / 1000).toString(),
      formatValue(record.totalPayment),
      formatValue(record.drfAdjustment),
      formatValue(record.drfEndingBalance),
    ];
  });

  // Calculate totals for footer row
  const totals = {
    totalRevenue: sortedPlan.reduce((sum, r) => sum + r.totalRevenue, 0),
    totalExistingDebt: sortedPlan.reduce((sum, r) => sum + r.existingDebt, 0),
    totalNetRevenue: sortedPlan.reduce((sum, r) => sum + r.netRevenue, 0),
    totalBondPayments: sortedPlan.reduce((sum, r) => sum + r.totalBondPayments, 0),
    totalPayments: sortedPlan.reduce((sum, r) => sum + r.totalPayment, 0),
    totalDrfAdjustment: sortedPlan.reduce((sum, r) => sum + r.drfAdjustment, 0),
    finalDrfBalance: sortedPlan[sortedPlan.length - 1]?.drfEndingBalance ?? 0,
  };

  const bondTotalPairs = bondNames.flatMap((_, index) => {
    const total = sortedPlan.reduce((sum, r) => sum + (r.bondPayments?.[index] ?? 0), 0);
    return [formatValue(total), Math.round(total / 1000).toString()];
  });

  // Add totals row
  rows.push([
    'TOTAL',
    '', // No total for taxable value
    '', // No total for millage
    formatValue(totals.totalRevenue),
    formatValue(totals.totalExistingDebt),
    formatValue(totals.totalNetRevenue),
    ...bondTotalPairs,
    formatValue(totals.totalBondPayments),
    Math.round(totals.totalBondPayments / 1000).toString(),
    formatValue(totals.totalPayments),
    formatValue(totals.totalDrfAdjustment),
    formatValue(totals.finalDrfBalance),
  ]);

  // Format as markdown table
  const markdownTable = formatMarkdownTable(headers, rows, {
    align: 'auto',
    minColumnWidth: 3,
    maxColumnWidth: 50,
  });

  // Build the formatted text
  const textLines = [
    '='.repeat(80),
    'TAX YEAR PLAN INFORMATION',
    '='.repeat(80),
    '',
    `Client: ${clientName}`,
    `Project: ${projectName}`,
    `Shape ID: ${shapeId}`,
    `Configuration: ${configuration.name || `Config ${configuration.orderId || 1}`}`,
    `Number of Bonds: ${bondNames.length}`,
    '',
    '```markdown',
    markdownTable,
    '```',
    '',
    `Total Years: ${sortedPlan.length}`,
    `Tax Year Range: ${sortedPlan[0]?.taxYear || 'N/A'} to ${
      sortedPlan[sortedPlan.length - 1]?.taxYear || 'N/A'
    }`,
  ];

  return textLines.join('\n');
}

/**
 * Copy cash flow table to clipboard
 */
export async function copyCashFlowTableToClipboard(
  candidate: Candidate,
  project: ProjectData,
  configuration: Configuration
): Promise<void> {
  const text = generateCashFlowTableText(candidate, project, configuration);
  await navigator.clipboard.writeText(text);
}

/**
 * Copy tax year plan table to clipboard
 */
export async function copyTaxYearPlanTableToClipboard(
  candidate: Candidate,
  project: ProjectData,
  configuration: Configuration
): Promise<void> {
  const text = generateTaxYearPlanTableText(candidate, project, configuration);
  await navigator.clipboard.writeText(text);
}
